// ─────────────────────────────────────────────────────────────────────────────
// Asignación masiva de roles (minuta 2026-10-20): dar el mismo rol, alcance y permisos de
// Brillamos a n personas de una sola vez (p. ej. al equipo de Capital Humano de un sitio, para
// «reconocer a nombre de» y la carga masiva por Excel).
//
// El backend sigue siendo uno a uno (PUT v1/users/{id}/recognition-role); aquí solo se reparte
// el trabajo con concurrencia acotada y se junta el resultado por persona, para que un fallo
// aislado no tumbe el resto ni se pierda en silencio.
// ─────────────────────────────────────────────────────────────────────────────
import { userAdminApi } from '@/api/client';
import type { UserSearchSelection } from '@/components/common';

/** Llamadas simultáneas al guardar o al resolver correos: suficiente para 100 personas en pocos
 *  segundos sin saturar al servicio. */
export const CONCURRENCIA_LOTE = 4;

export interface FalloDeLote<T> {
  item: T;
  error: unknown;
}

/**
 * Ejecuta `tarea` sobre cada elemento con a lo más `concurrencia` en vuelo. Nunca rechaza: devuelve
 * los que fallaron con su error. `onAvance` recibe cuántos van terminados (bien o mal).
 */
export async function ejecutarEnLote<T>(
  items: T[],
  tarea: (item: T) => Promise<unknown>,
  onAvance?: (terminados: number) => void,
  concurrencia: number = CONCURRENCIA_LOTE,
): Promise<FalloDeLote<T>[]> {
  const fallos: FalloDeLote<T>[] = [];
  let siguiente = 0;
  let terminados = 0;
  async function trabajador() {
    while (siguiente < items.length) {
      const item = items[siguiente++] as T;
      try {
        await tarea(item);
      } catch (error) {
        fallos.push({ item, error });
      }
      terminados += 1;
      onAvance?.(terminados);
    }
  }
  const hilos = Math.max(1, Math.min(concurrencia, items.length));
  await Promise.all(Array.from({ length: hilos }, trabajador));
  // Mismo orden que la lista original, aunque terminen desordenados.
  return fallos.sort((a, b) => items.indexOf(a.item) - items.indexOf(b.item));
}

export interface CorreosResueltos {
  encontrados: UserSearchSelection[];
  noEncontrados: string[];
}

/**
 * Busca a cada correo en el directorio (mismo endpoint que el buscador del modal) y se queda solo
 * con la coincidencia EXACTA del correo: la búsqueda es por subcadena y «ana@sigma.com» también
 * traería a «mariana@sigma.com».
 */
export async function resolverCorreos(correos: string[]): Promise<CorreosResueltos> {
  const encontrados = new Map<string, UserSearchSelection>();
  const noEncontrados: string[] = [];
  const fallos = await ejecutarEnLote(correos, async (correo) => {
    const respuesta = await userAdminApi.getUsers({ search: correo, pageSize: 10, includeUnclaimed: true, isActive: true });
    const exacto = (respuesta.items ?? []).find((usuario) => usuario.email?.toLowerCase() === correo);
    if (!exacto) {
      noEncontrados.push(correo);
      return;
    }
    encontrados.set(correo, { id: exacto.id, email: exacto.email, displayName: exacto.displayName ?? exacto.email ?? exacto.id });
  });
  // Un correo cuya búsqueda falló por red tampoco se pudo agregar: se reporta igual que uno
  // inexistente, para que el administrador lo vea y lo reintente.
  noEncontrados.push(...fallos.map((fallo) => fallo.item));
  return {
    encontrados: correos.flatMap((correo) => encontrados.get(correo) ?? []),
    noEncontrados: correos.filter((correo) => noEncontrados.includes(correo)),
  };
}
